/**
 * WHAT LEVERI UNDERSTOOD — the wire shape of an interpretation.
 *
 * An accountant describes a problem in their own words. Leveri reads it,
 * decides what kind of professional work it is, and either proposes a plan it
 * can actually perform, asks one professional question, or says plainly that
 * this is not work it can do. Those three, and no fourth.
 *
 * THE SAME LAW AS EVERY OTHER CONTRACT HERE: the browser renders; it never
 * derives. Nothing below is a raw score, a percentage or an enum a screen has
 * to interpret. Confidence arrives as a word and a tone because a number a
 * screen turns into a word is the screen deciding how sure Leveri is.
 *
 * AND ONE THING IS ABSENT ON PURPOSE. There is no place in this shape for an
 * accounting Fact, a Decision, an EvidenceRequirement, a conclusion, a figure
 * or a governed status — because a model's reading of a sentence may never
 * become any of those. Those are produced by the professional core from
 * evidence, through the governed path, or they do not exist.
 */
import { z } from "zod";
import { Uuid } from "./primitives.js";
import { ControlCandidate, Named, RecordKind, Tone } from "./vocabulary.js";
/** Three outcomes. A fourth would be a way of proceeding without one of these being true. */
export const IntentDisposition = z.enum([
    "PLANNED", // Leveri knows what this work is and can perform it
    "NEEDS_CLARIFICATION", // not sure enough to proceed; one question is asked
    "OUT_OF_SCOPE", // understood, and not work Leveri performs
]);
/**
 * HOW SURE LEVERI IS, as a word.
 *
 * The score that produced it is kept in provenance, where it can be audited,
 * and is deliberately not sent: a figure on a screen invites an accountant to
 * do arithmetic with a confidence, and there is no arithmetic to do.
 */
export const ConfidenceBand = z.object({
    band: z.enum(["HIGH", "MODERATE", "LOW"]),
    label: z.string(),
    tone: Tone,
});
/** What Leveri needs answered before it can proceed, and the choices where there are choices. */
export const IntentQuestion = z.object({
    kind: z.enum(["WORK_KIND", "CONTROL_ACCOUNT", "RECORDS", "RESTATE"]),
    question: z.string(),
    detail: z.string().nullable(),
    options: z.array(Named),
});
/**
 * THE PLAN LEVERI PROPOSES — a proposal, never an act.
 *
 * Nothing here has run. The plan names the frozen capability it would pin, the
 * records it needs, the control line it would reconcile, and — importantly —
 * the boundaries of what that capability will and will not conclude, so an
 * accountant knows before it runs that explaining a difference is not the same
 * as supporting its accounting treatment.
 */
export const CandidatePlan = z.object({
    capabilityRef: z.string(),
    capabilityLabel: z.string(),
    summary: z.string(),
    steps: z.array(z.string()),
    /** The frozen version this plan would pin, exactly as the published record names it. */
    pinnedVersionRef: z.string(),
    recordsNeeded: z.array(RecordKind),
    recordsHeld: z.array(RecordKind),
    /**
     * The control line, when Leveri could settle it from the records themselves.
     * Null means the choice is still the accountant's — never a guess.
     */
    control: ControlCandidate.nullable(),
    /** What this capability will NOT conclude, stated before it runs. */
    boundaries: z.array(z.string()),
});
/** Which model read it, under which policy, and the hash of what it said. */
export const IntentProvenance = z.object({
    provider: z.string(),
    providerLabel: z.string(),
    model: z.string(),
    policyVersion: z.string(),
    resultHash: z.string(),
    interpretedAt: z.string(),
    interpretedAtText: z.string(),
});
export const Interpretation = z.object({
    id: Uuid,
    /** The work it belongs to, when it was read in the context of one. */
    workId: Uuid.nullable(),
    instruction: z.string(),
    disposition: IntentDisposition,
    /** What Leveri took the accountant to mean, restated professionally. */
    understanding: z.string(),
    /** The kind of work this is, in words — stated even when Leveri cannot perform it. */
    recognisedAs: z.string(),
    confidence: ConfidenceBand,
    plan: CandidatePlan.nullable(),
    question: IntentQuestion.nullable(),
    /** Why Leveri will not perform it — its own sentence, never a stack trace. */
    unsupportedReason: z.string().nullable(),
    /** What Leveri CAN do, offered rather than leaving a person at a dead end. */
    offered: z.array(Named),
    provenance: IntentProvenance,
});
export const InterpretRequest = z.object({
    instruction: z.string().trim().min(3, "Say what the work is").max(2000),
    /** Optional: an engagement grounds the reading in a client and a period. */
    engagementId: Uuid.optional(),
});
export const InterpretationResponse = z.object({ interpretation: Interpretation });
/**
 * ADR-0018 — A MODEL'S PROPOSED READING OF AN OPEN EXCEPTION, as a person sees it.
 *
 * Every string here is Leveri's: the model's own prose appears only inside
 * `reading`, `statement` and `purpose`, after the gate admitted it, and always
 * under `headline`, which says it is a proposal that changed nothing. A
 * refused or unavailable reading shows WHY and never the answer.
 */
export const InvestigationDisposition = z.enum(["ADMITTED", "REFUSED", "UNAVAILABLE"]);
export const InvestigationPhase = z.enum(["CAUSE_OPEN", "TREATMENT_OPEN"]);
export const Investigation = z.object({
    id: Uuid,
    workId: Uuid,
    phase: InvestigationPhase,
    phaseText: z.string(),
    versionSeq: z.number().int().positive(),
    /** False once a later version exists: the reading was of an earlier state. */
    current: z.boolean(),
    provider: z.string(),
    model: z.string(),
    readerText: z.string(),
    disposition: InvestigationDisposition,
    headline: z.string(),
    reading: z.string().nullable(),
    /** investigation-policy@10: why the model's summary was withheld from display (over its length limit); null when shown. */
    readingWithheld: z.string().nullable().default(null),
    /** Non-null when no explanation is justified to lead: "No explanation is currently favoured by the evidence." (owner ruling 2026-10-07). */
    rankingText: z.string().nullable().default(null),
    /** The heading over the explanations; "Possible explanations — not ranked." when none leads, and they are then in a neutral order. */
    explanationsLabel: z.string().default("Possible explanations"),
    hypotheses: z.array(z.object({
        label: z.string(),
        statement: z.string(),
        /** True where the sentence is Leveri's wording of the structured cause, not the model's (investigation-policy@8). */
        wordedByLeveri: z.boolean().default(false),
        leading: z.boolean(),
        citations: z.array(z.string()),
        settledBy: z.array(z.string()),
    })),
    needs: z.array(z.object({ label: z.string(), purpose: z.string(), citations: z.array(z.string()) })),
    agreementText: z.string().nullable(),
    /** Explanations Leveri removed (impossible by the arithmetic or the observed leg), with the reason — never the removed text. */
    removed: z.array(z.object({ label: z.string(), reason: z.string() })).default([]),
    refusedBecause: z.array(z.string()),
    unavailableReason: z.string().nullable(),
    usageText: z.string().nullable(),
    askedByName: z.string(),
    createdAt: z.string(),
    createdAtText: z.string(),
});
export const InvestigationResponse = z.object({ investigation: Investigation });
//# sourceMappingURL=intent.js.map